import { useCallback, useEffect, useState } from "react";
import { FaCompress, FaExpand } from "react-icons/fa";
import {
  AudioTrack,
  LocalTrack,
  RemoteTrackPublication,
  Room as RoomType,
} from "twilio-video";
import { useLocalParticipant } from "../../hooks/useLocalParticipant";
import { useRemoteParticipant } from "../../hooks/useRemoteParticipant";
import { useRoom } from "../../hooks/useRoom";
import { useVideoChat } from "../../hooks/useVideoChat";
import { getTrack } from "../../utils/getTrack";
import FocusedFullscreenButton from "../Buttons/FocusedFullscreenButton";
import FullscreenCloseButton from "../Buttons/FullscreenCloseButton";
import { getExistingAudioTrack } from "../helpers/getExistingTrack";
import RemoteParticipantOptions from "../RemoteParticipantOptions/RemoteParticipantOptions";
import VideoInfo from "../VideoInfo/VideoInfo";
import VideoOffIndicator from "../VideoOffIndicator/VideoOffIndicator";
import { FocusedVideo, FocusedWrapper } from "./FocusedParticipant.styles";

interface FocusedParticipantProps {
  room: RoomType;
}

const FocusedParticipant = ({ room }: FocusedParticipantProps) => {
  const { currentFocused, focusedVideoOff, focusedParticipantRef } = useRoom();
  const { permission } = useVideoChat();
  const { remoteParticipant, isOnBackground } = useRemoteParticipant();
  const { audioOff } = useLocalParticipant();

  const [fullscreen, setFullscreen] = useState<boolean>(false);
  const [focusedAudioOff, setFocusedAudioOff] = useState<boolean>(false);
  const [isFrontFacing, setIsFrontFacing] = useState<boolean>(true);
  const [screenShareOwnerName, setScreenShareOwnerName] = useState<string | null>(null);

  const handleFullscreen = useCallback(() => {
    setFullscreen((prevState) => !prevState);
  }, []);

  const isScreenFromLocalParticipant = useCallback(() => {
    const localScreenTrack = Array.from(room.localParticipant.videoTracks.values()).find(
      (trackPub) => trackPub.track.name === "screen"
    );

    return currentFocused === "screen" && !!localScreenTrack;
  }, [room.localParticipant.videoTracks, currentFocused]);

  const handleFocusedName = () => {
    if (currentFocused === "screen") {
      if (isScreenFromLocalParticipant()) {
        return "Sua tela";
      }

      return screenShareOwnerName ? `Tela de ${screenShareOwnerName}` : "Tela compartilhada";
    }

    if (currentFocused === "local") {
      return "Você";
    }

    const identity = remoteParticipant?.identity || "";
    const name = identity.split("-").slice(1, -1).join("-");
    return name;
  };

  useEffect(() => {
    const handleTrackFrontFacing = (track: LocalTrack) => {
      if (track.kind === "video") {
        const mediaStreamTrackSettings = track.mediaStreamTrack.getSettings();
        setIsFrontFacing(mediaStreamTrackSettings.facingMode === "user");
      }
    };

    room.localParticipant.on("trackStarted", handleTrackFrontFacing);

    room.localParticipant.videoTracks.forEach((videoTrack) =>
      handleTrackFrontFacing(videoTrack.track)
    );

    return () => {
      room.localParticipant.removeListener("trackStarted", handleTrackFrontFacing);
    };
  }, [room]);

  useEffect(() => {
    const handleAudio = (track: RemoteTrackPublication) => {
      if (currentFocused === "remote" && track && track.kind === "audio") {
        setFocusedAudioOff(!track.isTrackEnabled);
      }
    };

    if (remoteParticipant) {
      const audioTrack = getTrack(
        "microphone",
        getExistingAudioTrack(remoteParticipant)
      ) as AudioTrack;
      if (currentFocused === "remote" && audioTrack) {
        setFocusedAudioOff(!audioTrack.isEnabled);
      }

      remoteParticipant.on("trackDisabled", handleAudio);
      remoteParticipant.on("trackEnabled", handleAudio);
    }

    return () => {
      if (remoteParticipant) {
        remoteParticipant.removeListener("trackDisabled", handleAudio);
        remoteParticipant.removeListener("trackEnabled", handleAudio);
        if (currentFocused === "remote") {
          setFocusedAudioOff(false);
        }
      }
    };
  }, [remoteParticipant, currentFocused]);

  useEffect(() => {
    if (currentFocused === "local") {
      setFocusedAudioOff(audioOff);
    }

    return () => {
      if (currentFocused === "local") {
        setFocusedAudioOff(false);
      }
    };
  }, [audioOff, currentFocused]);

  useEffect(() => {
    const handlerMap = new Map<string, (publication: RemoteTrackPublication) => void>();

    room.participants.forEach((participant) => {
      participant.tracks.forEach((publication) => {
        if (publication.trackName === "screen" && publication.track) {
          const parts = participant.identity?.split("-") || [];
          const name = parts.length >= 3 ? parts.slice(1, -1).join("-") : "Participante";
          setScreenShareOwnerName(name);
        }
      });

      const handler = (publication: RemoteTrackPublication) => {
        if (publication.trackName === "screen" && publication.track) {
          const parts = participant.identity?.split("-") || [];
          const name = parts.length >= 3 ? parts.slice(1, -1).join("-") : "Participante";
          setScreenShareOwnerName(name);
        }
      };

      handlerMap.set(participant.sid, handler);
      participant.on("trackPublished", handler);
    });

    return () => {
      room.participants.forEach((participant) => {
        const handler = handlerMap.get(participant.sid);
        if (handler) {
          participant.removeListener("trackPublished", handler);
        }
      });
    };
  }, [room]);

  useEffect(() => {
    if (currentFocused !== "screen") return;
  
    let nameSet = false;
  
    room.participants.forEach((participant) => {
      const isScreenSharing = Array.from(participant.videoTracks.values()).some(
        (pub) => pub.trackName === "screen" && pub.track
      );
  
      if (isScreenSharing) {
        const name = participant.identity?.split("-").slice(1, -1).join("-") || "Participante";
        setScreenShareOwnerName(name);
        nameSet = true;
      }
    });
  
    if (!nameSet) {
      setScreenShareOwnerName(null);
    }
  }, [currentFocused, room]);
  

  return (
    <FocusedWrapper
      fullscreen={fullscreen}
      onClick={fullscreen ? () => setFullscreen(false) : undefined}
    >
      <FocusedVideo
        hidden={focusedVideoOff}
        isScreen={currentFocused === "screen"}
        ref={focusedParticipantRef}
        style={
          isFrontFacing && currentFocused === "local"
            ? { transform: "translate(-50%) scaleX(-1)" }
            : {}
        }
        autoPlay
        playsInline
        disablePictureInPicture
        onClick={(e) => e.stopPropagation()}
        muted
      />

      {currentFocused !== "screen" && (
        <VideoOffIndicator
          message={
            isOnBackground && currentFocused === "remote"
              ? "Paciente está com a telemedicina em segundo plano.."
              : undefined
          }
        />
      )}

      {currentFocused === "screen" && (
        <FocusedFullscreenButton
          handleClick={handleFullscreen}
          Icon={fullscreen ? FaCompress : FaExpand}
        />
      )}

      {fullscreen && (
        <FullscreenCloseButton handleClick={() => setFullscreen(false)} />
      )}

      <VideoInfo
        name={handleFocusedName() || ""}
        videoType={currentFocused}
        isAudioOff={focusedAudioOff}
      />

      {currentFocused === "remote" && <RemoteParticipantOptions />}
    </FocusedWrapper>
  );
};

export default FocusedParticipant;
